/**
 * <bc-cart-upsell> — inline upsell inside the Rebuy Smart Cart line items.
 *
 * Placed in the Rebuy cart-items template:
 *   <li class="bc-upsell-slot" role="listitem" hidden>
 *     <bc-cart-upsell data-source-id="325867" data-limit="3" data-title="Complete your bed"></bc-cart-upsell>
 *   </li>
 *
 * Theme settings arrive via window.bcCartUpsellConfig (snippets/rebuy-extensions.liquid).
 */
(() => {
  if (customElements.get('bc-cart-upsell')) return;

  const config = window.bcCartUpsellConfig || {};
  const MAX_SWATCHES = 6;
  const SWATCH_OPTION_NAMES = String(config.swatchOptionNames || '')
    .split(',').map(name => name.trim().replace(/:$/, '').toLowerCase()).filter(Boolean);
  const SWATCH_COLOURS = {};
  String(config.swatchValues || '').split('\n').forEach(line => {
    const i = line.indexOf(':');
    if (i > 0) SWATCH_COLOURS[line.slice(0, i).trim().toLowerCase()] = line.slice(i + 1).trim();
  });

  // Data source responses keyed by the cart's lines, shared across re-renders
  const cache = new Map();

  const money = n => '$' + n.toFixed(2).replace(/\.00$/, '');
  const esc = str => String(str ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const sizedImage = (src, width) => src ? `${src}${src.includes('?') ? '&' : '?'}width=${width}` : '';
  const isAvailable = v => v.available !== false && (v.inventory_management !== 'shopify' || v.inventory_policy === 'continue' || v.inventory_quantity > 0);
  const optionsOf = (product, v) => [v.option1, v.option2, v.option3].slice(0, (product.options || []).length || 1);
  const isSwatchOption = (option, values) => SWATCH_OPTION_NAMES.includes(String(option.name).toLowerCase()) && values.length <= MAX_SWATCHES;

  function swatchStyle(value) {
    const colour = SWATCH_COLOURS[String(value).toLowerCase()];
    if (colour && colour.startsWith('#')) return `--swatch-background-color:${colour}`;
    return window.rebuyExtensions?.getURL ? `--swatch-background-image:url(${window.rebuyExtensions.getURL(value)})` : '';
  }

  class BcCartUpsell extends HTMLElement {
    constructor() {
      super();
      this.products = [];
      this.selected = new Map();

      this.addEventListener('click', event => {
        const swatch = event.target.closest('.bcu__swatch');
        if (swatch) this.selectOption(+swatch.dataset.card, +swatch.dataset.option, swatch.dataset.value);
        const add = event.target.closest('.bcu__add');
        if (add) this.add(+add.dataset.card, add);
      });

      this.addEventListener('change', event => {
        const select = event.target.closest('.bcu__select');
        if (select) this.selectOption(+select.dataset.card, +select.dataset.option, select.value);
      });

      this.onCartChange = () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.load(), 200);
      };
    }

    connectedCallback() {
      this.wrapper = this.closest('.bc-upsell-slot') || this;
      document.addEventListener('rebuy:cart.change', this.onCartChange);
      this.load();
    }

    disconnectedCallback() {
      document.removeEventListener('rebuy:cart.change', this.onCartChange);
      clearTimeout(this.timer);
      this.destroySlider();
    }

    async load() {
      const limit = Math.min(parseInt(this.dataset.limit, 10) || 1, 3);
      let key;

      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        const items = cart.items || [];
        if (!items.length) return this.hide();

        const productIds = [...new Set(items.map(item => String(item.product_id)))];
        key = items.map(item => `${item.variant_id}x${item.quantity}`).sort().join(',');

        if (!cache.has(key)) {
          const params = new URLSearchParams({
            key: config.rebuyKey,
            shopify_product_ids: productIds.join(','),
            shopify_variant_ids: items.map(item => item.variant_id).join(','),
            limit: String(limit + productIds.length),
            'cart[token]': cart.token || '',
            'cart[subtotal]': String(cart.total_price || 0),
            'cart[item_count]': String(cart.item_count || 0),
            'cart[line_count]': String(items.length),
          });
          // Cart rules in the data source only match when the cart lines are sent in this shape
          items.forEach((item, i) => {
            params.set(`cart[items][${i}][product_id]`, item.product_id);
            params.set(`cart[items][${i}][variant_id]`, item.variant_id);
            params.set(`cart[items][${i}][quantity]`, item.quantity);
          });
          cache.set(key, fetch(`https://rebuyengine.com/api/v1/custom/id/${this.dataset.sourceId}?${params}`)
            .then(r => r.json())
            .then(json => json.data || []));
        }

        const data = await cache.get(key);
        if (!this.isConnected) return;

        const cartSizes = new Set(items.flatMap(item => (item.options_with_values || [])
          .filter(option => /size/i.test(option.name))
          .map(option => option.value)));

        this.products = data
          .filter(product => !productIds.includes(String(product.id)) && (product.variants || []).some(isAvailable))
          .slice(0, limit)
          .map(product => ({ product, variant: this.initialVariant(product, cartSizes) }));

        this.products.length ? this.render() : this.hide();
      } catch (error) {
        if (key) cache.delete(key);
        this.hide();
      }
    }

    // Keep a previous selection, else match a size already in the cart, else the first available variant
    initialVariant(product, cartSizes) {
      const available = product.variants.filter(isAvailable);
      const savedId = this.selected.get(String(product.id));
      const sizeIndex = (product.options || []).findIndex(option => /size/i.test(option.name));

      return available.find(v => v.id === savedId)
        || (sizeIndex > -1 && available.find(v => cartSizes.has(optionsOf(product, v)[sizeIndex])))
        || available[0];
    }

    hide() {
      this.destroySlider();
      this.products = [];
      this.innerHTML = '';
      this.wrapper.hidden = true;
    }

    render() {
      const multi = this.products.length > 1;
      const title = this.dataset.title || 'Complete your bed';

      this.destroySlider();
      this.setAttribute('role', 'region');
      this.setAttribute('aria-label', title);
      if (multi) this.setAttribute('aria-roledescription', 'carousel');
      else this.removeAttribute('aria-roledescription');

      this.innerHTML = `
        <div class="bcu__header">
          <p class="bcu__title">${esc(title)}</p>
          ${multi ? '<div class="bcu__arrows"></div>' : ''}
        </div>
        <div class="bcu__track">
          ${this.products.map((entry, i) => `
            <div class="bcu__card" data-card="${i}"${multi ? ` role="group" aria-roledescription="slide" aria-label="${i + 1} of ${this.products.length}"` : ''}>
              ${this.cardHtml(entry, i)}
            </div>`).join('')}
        </div>
        ${multi ? '<div class="bcu__dots"></div>' : ''}
      `;
      this.wrapper.hidden = false;

      if (multi) this.initSlider();
    }

    // Theme's slick (vendor.min.js). Without it the cards still render, stacked in a native scroll track.
    initSlider() {
      const $ = window.jQuery;
      if (!$ || !$.fn.slick) return this.querySelector('.bcu__track').classList.add('bcu__track--scroll');

      const strings = window.theme?.strings || {};
      const icons = window.theme?.icons || {};
      const $track = $(this.querySelector('.bcu__track'));

      // Slick 1.6 marks slides up as a listbox once it has initialised; restore the carousel/slide semantics set in render()
      const a11yFix = slick => {
        slick.$slideTrack.removeAttr('role');
        slick.$slides.attr('role', 'group').removeAttr('aria-describedby');
        slick.$list.attr('aria-live', 'polite');
        // Only the visible card's controls are tabbable (none of them carry a tabindex of their own)
        slick.$slides.each((i, slide) => slide.querySelectorAll('a, button, select').forEach(control => {
          if (i === slick.currentSlide) control.removeAttribute('tabindex');
          else control.setAttribute('tabindex', '-1');
        }));
      };
      // Slick re-applies its roles straight after afterChange, so run once it's done
      $track.on('afterChange', (event, slick) => setTimeout(() => a11yFix(slick)));

      $track.slick({
        accessibility: true,
        infinite: false,
        slidesToShow: 1.2,
        slidesToScroll: 1,
        arrows: true,
        dots: true,
        appendArrows: $(this.querySelector('.bcu__arrows')),
        appendDots: $(this.querySelector('.bcu__dots')),
        prevArrow: `<button type="button" class="bcu__arrow bcu__arrow--prev" aria-label="${esc(strings.previous || 'Previous')}">${icons.chevronLeft || '&lsaquo;'}</button>`,
        nextArrow: `<button type="button" class="bcu__arrow bcu__arrow--next" aria-label="${esc(strings.next || 'Next')}">${icons.chevronRight || '&rsaquo;'}</button>`,
        customPaging: (slick, i) => `<button type="button" class="bcu__dot" aria-label="Show product ${i + 1} of ${slick.slideCount}"></button>`,
        rtl: !!document.querySelector('html[dir=rtl]'),
      });
      a11yFix($track.slick('getSlick'));
      this.$track = $track;
    }

    destroySlider() {
      if (!this.$track) return;
      this.$track.off('afterChange');
      if (this.$track.hasClass('slick-initialized')) this.$track.slick('unslick');
      this.$track = null;
    }

    cardHtml({ product, variant }, i) {
      const price = parseFloat(variant.price) || 0;
      const compare = parseFloat(variant.compare_at_price) || 0;
      const onSale = compare > price;
      const images = product.images || [];
      const image = images.find(img => img.id === variant.image_id) || product.image || images[0] || {};
      const selected = optionsOf(product, variant);
      const url = `/products/${product.handle}?variant=${variant.id}`;
      const labels = [];

      const optionsHtml = (product.options || []).map((option, idx) => {
        // Data source rules can return a subset of variants, so only offer values those variants cover
        const values = (option.values || []).filter(value => product.variants.some(v => optionsOf(product, v)[idx] === value));
        if (values.length < 2) {
          if (selected[idx] && selected[idx] !== 'Default Title') labels.push(selected[idx]);
          return '';
        }

        const hasStock = value => product.variants.some(v => isAvailable(v) && optionsOf(product, v)[idx] === value);

        if (isSwatchOption(option, values)) {
          labels.push(selected[idx]);
          return `<div class="bcu__swatches" role="radiogroup" aria-label="${esc(option.name)}">${values.map(value => `
            <button type="button" class="bcu__swatch${value === selected[idx] ? ' is-active' : ''}" role="radio"
              aria-checked="${value === selected[idx]}" aria-label="${esc(value)}" title="${esc(value)}"
              data-card="${i}" data-option="${idx}" data-value="${esc(value)}"
              style="${esc(swatchStyle(value))}"${hasStock(value) ? '' : ' disabled'}></button>`).join('')}
          </div>`;
        }

        return `<label><span class="bcu__sr">${esc(option.name)}</span>
          <select class="bcu__select" data-card="${i}" data-option="${idx}">${values.map(value => `
            <option value="${esc(value)}"${value === selected[idx] ? ' selected' : ''}${hasStock(value) ? '' : ' disabled'}>${esc(value)}</option>`).join('')}
          </select></label>`;
      }).join('');

      return `
          <div class="bcu__main">
            <div class="bcu__media">
              <img class="bcu__image" src="${esc(sizedImage(image.src, 200))}" alt="${esc(image.alt || product.title)}" width="75" height="75" loading="lazy">
              ${onSale ? `<span class="bcu__badge">Save ${Math.round((compare - price) / compare * 100)}%</span>` : ''}
            </div>
            <div class="bcu__info">
              <a class="bcu__name" href="${esc(url)}">${esc(product.title)}</a>
              ${labels.length ? `<p class="bcu__variant">${esc(labels.join(' / '))}</p>` : ''}
              <div class="bcu__price">
                <span class="${onSale ? 'bcu__price--sale' : 'bcu__price--regular'}"><span class="bcu__sr">${onSale ? 'Sale price' : 'Price'}</span>${money(price)}</span>
                ${onSale ? `<span class="bcu__price--compare"><span class="bcu__sr">Original price</span>${money(compare)}</span>` : ''}
              </div>
            </div>
          </div>
          <div class="bcu__footer">
            ${optionsHtml ? `<div class="bcu__options">${optionsHtml}</div>` : ''}
            <button type="button" class="bcu__add button button--secondary" data-card="${i}" aria-label="Add ${esc(product.title)} to cart">Add</button>
          </div>
      `;
    }

    selectOption(i, idx, value) {
      const entry = this.products[i];
      if (!entry) return;

      const { product } = entry;
      const wanted = optionsOf(product, entry.variant);
      wanted[idx] = value;
      const available = product.variants.filter(isAvailable);

      entry.variant = available.find(v => optionsOf(product, v).every((o, k) => o === wanted[k]))
        || available.find(v => optionsOf(product, v)[idx] === value)
        || entry.variant;
      this.selected.set(String(product.id), entry.variant.id);

      this.querySelector(`.bcu__card[data-card="${i}"]`).innerHTML = this.cardHtml(entry, i);
      const control = this.querySelector(`.bcu__card[data-card="${i}"] .bcu__swatch.is-active[data-option="${idx}"], .bcu__card[data-card="${i}"] .bcu__select[data-option="${idx}"]`);
      if (control) control.focus();
    }

    async add(i, button) {
      const entry = this.products[i];
      if (!entry) return;

      button.disabled = true;
      button.textContent = 'Adding…';

      try {
        await window.Rebuy.Cart.addItem({ id: entry.variant.id, quantity: 1 });
        button.textContent = 'Added';
        this.onCartChange();
      } catch (error) {
        button.textContent = 'Try again';
        button.disabled = false;
      }
    }
  }

  customElements.define('bc-cart-upsell', BcCartUpsell);
})();
